<template>
  <div id="cfs-assumptions-modal" style="font-family: 'IBM Plex Sans', Raleway, serif">
    <div
      class="cursor-pointer mt-4 flex gap-2 items-center"
      @click="
        () => {
          showModal = true
        }
      ">
      <arrow-side />
      <span class="text-cfs-blue mp2 md:p2"> How we worked this out </span>
      <info-circle css-class="cfs-tooltip" fill="cfs-blue" message="Click here!" />
    </div>

    <modal
      v-if="showModal"
      id="cfs-assumption-modal"
      container-styles="p-5"
      @close="
        () => {
          showModal = false
        }
      ">
      <template #header>
        <div class="mh4 md:h4" style="font-family: 'IBM Plex Sans', Raleway, serif">
          Change assumptions
        </div>
      </template>
      <template #body>
        <loader v-if="loading" />
        <div v-else style="font-family: 'IBM Plex Sans', Raleway, serif">
          <input-percentage
            class="py-3"
            name="inflation-rate"
            label="Inflation rate"
            :value="inflation"
            @input="setInflationRate"
            :min="0"
            :max="5" />
          <input-percentage
            class="py-3"
            name="wage-growth"
            label="Wage growth"
            :value="wageGrowth"
            @input="setWageGrowth"
            :min="0"
            :max="6" />
        </div>

        <!--    investment returns and tax    -->
        <div
          class="flex font-medium text-center py-2 pt-4 gap-3"
          style="font-family: 'IBM Plex Sans', Raleway, serif">
          <div class="w-1/4 md:min-w-3/10">Investment option</div>
          <div class="w-1/5">Investment return</div>
          <div class="w-1/5">Tax on earnings</div>
          <div class="w-1/5">Fees (%)</div>
        </div>
        <!-- investments -->
        <div
          v-for="(assumption, index) in assumptionValues"
          :key="`assumption-${index}`"
          class="py-3 flex gap-3 items-center"
          style="font-family: 'IBM Plex Sans', Raleway, serif">
          <div class="flex w-1/4 md:min-w-3/10">
            {{ getAssumptionName(assumption.type) }}
          </div>

          <input-percentage
            class="flex w-1/5"
            :value="assumption.rateOfReturn"
            name="Investment return"
            @input="
              (value) => {
                changeAssumptionReturn(`${assumption.type}`, value)
              }
            " />

          <input-percentage
            v-if="assumption.type === 'superFunds'"
            class="flex w-1/5"
            :name="`${assumption.type}-tax-earnings-${index}`"
            :value="assumption.taxOnEarnings"
            @input="
              (value) => {
                changeTaxOnEarnings(`${assumption.type}`, value)
              }
            "
            :min="0"
            :max="15" />
          <div v-else class="flex w-1/5 p-4" />

          <input-percentage
            v-if="assumption.type === 'superFunds' || assumption.type === 'superFundsDrawDown'"
            class="flex w-1/5"
            :name="`${assumption.type}-admin-fee-${index}`"
            :value="assumption.adminFee"
            @input="
              (value) => {
                changeAdminRate(`${assumption.type}`, value)
              }
            "
            :min="0"
            :max="5"
            :disabled="assumption.name.toLowerCase() === 'current investment'" />
          <div v-else class="flex w-1/5 p-4" />
        </div>
        <div style="font-family: 'IBM Plex Sans', Raleway, serif" class="flex flex-col gap-6 my-6">
          <input-currency
            name="Annual Insurance Premium"
            label="Annual Insurance Premium"
            :value="insurancePremium"
            @input="updateInsurancePremium"
            class="w-full" />
          <input-currency
            name="Annual Admin Fees"
            label="Annual Admin Fees"
            :value="annualAdminFees"
            @input="updateAnnualAdminFees"
            class="w-full" />
        </div>
      </template>

      <template #footer>
        <div>
          <div class="text-right" v-if="!loading">
            <custom-button
              class="p-3 w-32 button-2"
              @click="reRenderGraph()"
              colour="cfs-button"
              style="font-family: 'IBM Plex Sans', Raleway, serif">
              Save
            </custom-button>
          </div>
          <hr class="my-5" />
          <more-retirement-assumptions
            :plan="plan"
            style="font-family: 'IBM Plex Sans', Raleway, serif" />
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import CustomButton from '@/components/CustomButton/CustomButton.vue'
import Modal from '@/components/Modals/Modal.vue'
import InputPercentage from '@/components/BasicInputs/InputPercentage.vue'
import InfoCircle from '@/components/InfoCircle/InfoCircle.vue'
import { formatPercentage } from '@/lib/helpers'
import Loader from '@/components/Loader/Loader.vue'
import MoreRetirementAssumptions from '@/components/RetirementInvestment/Summary/MoreRetirementAssumptions.vue'
import ArrowSide from '@/components/icons/ArrowSide.vue'
import InputCurrency from '@/components/BasicInputs/InputCurrency.vue'
import { useToast } from '@/composables/useToast.ts'
import { useRetirementCalculatorStore } from '@/store/pinia/RetirementCalculatorStore'
import { computed, ref } from 'vue'
import { isCFSWhitelabel } from '@/composables/isCFSWhitelabel.ts'

const { errorToast } = useToast()

// Store setup
const retirementCalculatorStore = useRetirementCalculatorStore()

// State
const showModal = ref(false)
const loading = ref(false)

// Computed properties
const assumptionValues = computed(() => retirementCalculatorStore.getAssumptionValues)
const insurancePremium = computed(() => retirementCalculatorStore.getInsurancePremium)
const annualAdminFees = computed(() => retirementCalculatorStore.getAnnualAdminFees)
const wageGrowth = computed(() => retirementCalculatorStore.getWageGrowth)
const inflation = computed(() => retirementCalculatorStore.getInflation)
const plan = computed(() =>
  isCFSWhitelabel() ? 'CFS_RETIREMENT_INCOME_CALC' : 'OTIVO_RETIREMENT_INCOME_CALC'
)

// Methods
const getSelectedValue = (type: string): string => {
  const selected = assumptionValues.value.filter((el: any) => el.type === type)
  if (selected.length === 0) {
    return 'balanced'
  }
  return selected[0].value
}

const updateInsurancePremium = (value: number) => {
  retirementCalculatorStore.updateInsurancePremium(value)
}

const updateAnnualAdminFees = (value: number) => {
  retirementCalculatorStore.updateAnnualAdminFees(value)
}

const changeSelectedValue = (type: string, value: string, label: string) => {
  retirementCalculatorStore.updateRetCalcAssumptionValue({ type, value, label })
}

const changeAssumptionReturn = (type: string, value: number) => {
  retirementCalculatorStore.setRetCalcInvestReturn({ type, value })
}

const changeTaxOnEarnings = (type: string, value: number) => {
  retirementCalculatorStore.setRetCalcTaxOnEarnings({ type, value })
}

const changeAdminRate = (type: string, value: number) => {
  retirementCalculatorStore.setRetCalcInvestAdminFee({ type, value })
}

const setInflationRate = (value: number) => {
  retirementCalculatorStore.setInvestInflationRate(value)
}

const setWageGrowth = (value: number) => {
  retirementCalculatorStore.setInvestmentWageGrowth(value)
}

const setDiscountRate = (value: number) => {
  // Placeholder for discount rate functionality if needed
  console.warn('setDiscountRate called but not implemented in new architecture')
}

const reRenderGraph = async () => {
  loading.value = true
  try {
    const success = await retirementCalculatorStore.validateAndCalculate()
    if (success) {
      showModal.value = false
    } else {
      errorToast('Unable to update assumptions. Please check your inputs and try again.')
    }
    loading.value = false
  } catch (err) {
    loading.value = false
    errorToast('Error occurred, please try again')
    throw err
  }
}

const getTaxOnEarnings = (rate: number): string => {
  return rate ? formatPercentage(rate) : 'N/A'
}

const getAssumptionName = (type: string): string => {
  const typeNames: Record<string, string> = {
    shares: 'Shares',
    superFunds: 'Superannuation',
    superFundsDrawDown: 'Superannuation after retirement',
    cashAndFixed: 'Cash',
    managedFunds: 'Managed funds and unlisted',
    property: 'Investment property',
    alternative: 'Other',
  }
  return typeNames[type] || type
}

const assumptionArray = (assumption: any) => {
  const outputArray = [
    { value: 'cash', label: 'Cash' },
    { value: 'conservative', label: 'Conservative' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'balanced', label: 'Balanced' },
    { value: 'default', label: 'Starting assumption' },
    { value: 'growth', label: 'Growth' },
    { value: 'highGrowth', label: 'High Growth' },
  ]

  // Super product functionality removed with Vuex migration
  // This would require accessing the supers store which is not implemented in current architecture

  return outputArray
}
</script>

<style scoped>
.custom-percentage :deep(.input-label) {
  display: none;
}

.custom-percentage :deep(input) {
  text-align: center;
  font-weight: 500;
}

.hide-input-label :deep(.input-label) {
  display: none;
}
</style>
